import React, { useState } from 'react'
import s from './OwnerBookingPage.module.sass'

import { Title } from '@shared/ui/Typography/Title'
import { Text } from '@shared/ui/Typography/Text'
import Button from '@shared/ui/Actions/Button/Button'
import BookingsTable from '@pages/OwnerPersonalPage/ui/components/BookingsTable'
import BookingUnavailable from '@pages/OwnerPersonalPage/ui/components/BookingUnavailable'
import CreateMessageModal from '@pages/OwnerPersonalPage/ui/components/CreateMessageModal'
import DateRangeFilter from '@pages/OwnerPersonalPage/ui/components/DateRangeFilter'
import MonthSelect from '@pages/OwnerPersonalPage/ui/components/MonthSelect'
import ConfirmationDialog from '@shared/ui/Overlays/ConfirmationDialog'
import IconArrow from '@icons/icon-arrow-left--24px.svg'
import IconOwnerStatusModeration from '@icons/icon-owner-status-moderation.svg'
import IconOwnerStatusPublished from '@icons/icon-owner-status-published.svg'
import Select from '@shared/ui/Form/Single/Select/Select'
import { getDefaultField } from '@shared/lib/fields/getDefaultField'
import type { FieldSelect, SelectOption } from '@shared/types/fields/fields'

// статусы брони по сути тоже мок, т.к. полностью зависят от бека
export type BookingStatus = 'confirmed' | 'missed' | 'duplicate'

export const bookingStatusConfig: Record<BookingStatus, { filterLabel: string; tableLabel: string }> = {
  confirmed: {
    filterLabel: 'Подтверждена',
    tableLabel: 'Бронь Подтверждена',
  },
  missed: {
    filterLabel: 'Пропущена',
    tableLabel: 'Бронь пропущена',
  },
  duplicate: {
    filterLabel: 'Дубль',
    tableLabel: 'Дубль',
  },
}

export type MessageStatus = 'moderation' | 'published'

export const statusConfig = {
  moderation: {
    text: 'На модерации',
    icon: <IconOwnerStatusModeration />,
  },

  published: {
    text: 'На форме бронирования',
    icon: <IconOwnerStatusPublished />,
  },
}

export interface Booking {
  id: number
  guestName: string
  phone: string
  seats: string
  specialRequests: string
  date: string
  time: string
  status: BookingStatus
}

const mockBookings: Booking[] = [
  {
    id: 1,
    guestName: 'Вики',
    phone: '+7 916 808-11-42',
    seats: '2 места',
    specialRequests: 'Столик у окна, но у двери и подальше от бара',
    date: '2 апреля',
    time: '12:00',
    status: 'confirmed',
  },
  {
    id: 2,
    guestName: 'Хавьер',
    phone: '+7 916 808-11-42',
    seats: '5 мест',
    specialRequests: '',
    date: '8 апреля',
    time: '20:00',
    status: 'duplicate',
  },
  {
    id: 3,
    guestName: 'Хавьер',
    phone: '+7 916 808-11-42',
    seats: '5 мест',
    specialRequests: '',
    date: '8 апреля',
    time: '20:00',
    status: 'confirmed',
  },
  {
    id: 4,
    guestName: 'Кристина Владимировна',
    phone: '+7 916 808-11-42',
    seats: '4 места',
    specialRequests: '2 детских стула и если длинный комментарий то увеличиваем высоту',
    date: '6 апреля',
    time: '18:00',
    status: 'missed',
  },
  {
    id: 5,
    guestName: 'Бардем',
    phone: '+7 916 808-11-42',
    seats: '3 места',
    specialRequests: '',
    date: '12 апреля',
    time: '19:00',
    status: 'missed',
  },
  {
    id: 6,
    guestName: 'Барселона',
    phone: '+7 916 808-11-42',
    seats: '3 места',
    specialRequests: 'Нужно будет вынести торт',
    date: '6 апреля',
    time: '15:00',
    status: 'confirmed',
  },
]

// базовый тариф, условие с бека
const IS_BOOKING_PAGE_UNAVAILABLE = false

const OwnerBookingPage = (props) => {
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false)
  const [isCreateMessageModalVisible, setIsCreateMessageModalVisible] = useState(false)

  // сообщение для гостей
  const [guestInformationMessage, setGuestInformationMessage] = useState<string | null>(null)
  const [messageStatus, setMessageStatus] = useState<MessageStatus | null>(null)

  // статус брони
  const statusOptions = [
    { value: 'active', label: 'Активные', disabled: false },
    ...Object.keys(bookingStatusConfig).map((key) => ({
      value: key,
      label: bookingStatusConfig[key as keyof typeof bookingStatusConfig]?.filterLabel || key,
      disabled: false,
    })),
  ]
  const [bookingStatusField, setBookingStatusField] = useState<FieldSelect>(() =>
    getDefaultField('select', {
      name: 'booking-status-filter',
      settings: {
        options: statusOptions,
      },
      value: statusOptions[0],
    }),
  )

  // константы для функционирования пикера месяцев
  const nowDate = new Date()
  const currentYear = nowDate.getFullYear()
  const currentMonth = nowDate.getMonth() + 1
  const MIN_YEAR = 2025
  const MIN_MONTH = 1

  // стейты пикеров конкретных дат либо месяцев
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const [selectedMonth, setSelectedMonth] = useState<{ month: number; year: number; isEmpty: boolean }>(() => ({
    month: currentMonth,
    year: currentYear,
    isEmpty: false,
  }))
  const isAtMinMonth = selectedMonth.year === MIN_YEAR && selectedMonth.month === MIN_MONTH
  const isAtMaxMonth = selectedMonth.year === currentYear && selectedMonth.month === currentMonth

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setStartDate(start)
    setEndDate(end)
  }

  const handleCreateMessageClick = () => {
    setIsCreateMessageModalVisible(true)
  }

  const handleCloseCreateMessageModal = () => {
    setIsCreateMessageModalVisible(false)
  }

  const handleSendMessage = (message: string) => {
    setGuestInformationMessage(message)
    setMessageStatus('moderation')
  }

  const handleDeleteMessage = () => {
    setIsDeleteConfirmationVisible(true)
  }

  const handleConfirmDelete = () => {
    setGuestInformationMessage(null)
    setMessageStatus(null)
    setIsDeleteConfirmationVisible(false)
  }

  const handleRejectDelete = () => {
    setIsDeleteConfirmationVisible(false)
  }

  const handlePreviousMonth = () => {
    if (isAtMinMonth) return
    const prevMonth = selectedMonth.month - 1
    const month = prevMonth >= 1 ? prevMonth : 12
    const year = prevMonth >= 1 ? selectedMonth.year : selectedMonth.year - 1
    setSelectedMonth({ month, year, isEmpty: false })
  }

  const handleNextMonth = () => {
    if (isAtMaxMonth) return
    const nextMonth = selectedMonth.month + 1
    const month = nextMonth <= 12 ? nextMonth : 1
    const year = nextMonth <= 12 ? selectedMonth.year : selectedMonth.year + 1
    setSelectedMonth({ month, year, isEmpty: false })
  }

  if (IS_BOOKING_PAGE_UNAVAILABLE) {
    return <BookingUnavailable />
  }

  return (
    <div className={s['content-container']}>
      <div className={s.section}>
        <div className={s.header}>
          <Title tag='h2' sizes='h5'>
            Информация для гостей
          </Title>

          {guestInformationMessage && messageStatus && (
            <div className={s['status-badge']} data-status-type={messageStatus}>
              <div className={s['status-content']}>
                <Text sizes='S' className={s['status-text']}>
                  {statusConfig[messageStatus].text}
                </Text>

                {!!statusConfig[messageStatus].icon && (
                  <div className={s['status-icon']}>{statusConfig[messageStatus].icon}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {guestInformationMessage ? (
          <div className={s['guest-message-card']}>
            <div className={s['guest-message-content']}>
              <div className={s['guest-icon-container']}>
                <span className={s['guest-exclamation-icon']}>!</span>
              </div>

              <div className={s['guest-message-text-container']}>
                <Text sizes='M' className={s['guest-message-text']}>
                  {guestInformationMessage}
                </Text>
              </div>
            </div>

            <Button sizes='L' mode='white-border' onClick={handleDeleteMessage} className={s['guest-delete-button']}>
              Удалить сообщение
            </Button>
          </div>
        ) : (
          <div className={s['info-card']}>
            <div className={s['info-content']}>
              <Text sizes='M'>
                У вас предусмотрен депозит, есть дресс-код? <br />
                Предупредите своих гостей о важных нюансах на форме бронирования
              </Text>
            </div>

            <Button sizes='L' mode='white-border' onClick={handleCreateMessageClick}>
              Создать сообщение
            </Button>
          </div>
        )}
      </div>

      <div className={s.section}>
        <Title tag='h2' sizes='h5' className={s.header}>
          Бронирования
        </Title>

        <div className={s['bookings-card']}>
          <div className={s.filters}>
            <div className={s['filter-item']}>
              <Text sizes='M'>Статус брони</Text>
              <Select
                field={bookingStatusField}
                handleChange={(option: SelectOption) =>
                  setBookingStatusField((prev) => ({ ...prev, value: option, error: '' }))
                }
                className={s['booking-select']}
              />
            </div>

            <div className={s['filter-item']}>
              <Text sizes='M'>Месяц</Text>

              <MonthSelect
                value={selectedMonth}
                onChange={(val) => setSelectedMonth(val)}
                // пикер месяцев должен вырубаться, если есть выбранная конкретная дата/ренж в пикере дат
                disabled={!!startDate}
                className={s['booking-select']}
                hideDisabledValue
              />
            </div>

            <div className={s['filter-item']}>
              <Text sizes='M'>Дата или период</Text>

              <DateRangeFilter startDate={startDate} endDate={endDate} onDateRangeChange={handleDateRangeChange} />
            </div>
          </div>

          <BookingsTable bookings={mockBookings} className={s['bookings-table']} />

          {
            // кнопки должны пропадать, если есть выбранная конкретная дата/ренж в пикере дат
            !startDate && (
              <div className={s['pagination-buttons']}>
                <Button sizes='L' mode='ghost' onClick={handlePreviousMonth} disabled={isAtMinMonth}>
                  <IconArrow className={s['arrow-icon-left']} />

                  <Text sizes='M'>Предыдущий месяц</Text>
                </Button>

                <Button sizes='L' mode='ghost' onClick={handleNextMonth} disabled={isAtMaxMonth}>
                  <Text sizes='M'>Следующий месяц</Text>

                  <IconArrow className={s['arrow-icon-right']} />
                </Button>
              </div>
            )
          }
        </div>
      </div>

      <CreateMessageModal
        isVisible={isCreateMessageModalVisible}
        onClose={handleCloseCreateMessageModal}
        onPublish={handleSendMessage}
      />

      <ConfirmationDialog
        isVisible={isDeleteConfirmationVisible}
        title='Вы действительно хотите удалить сообщение с информацией для гостей?'
        confirmationText='Удалить'
        rejectionText='Отменить'
        onConfirm={handleConfirmDelete}
        onReject={handleRejectDelete}
      />
    </div>
  )
}

export default OwnerBookingPage
